import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatCurrency } from './formatCurrency';

// Logo loader - converts image to base64 for PDF embedding
const loadLogoAsBase64 = () => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = '/SplashBI-logo.svg';
  });
};

/**
 * SplashBI Quote PDF Generator
 * 
 * Generates Internal (detailed with SKUs) and Customer (summary) PDFs
 * 
 * FIXES IMPLEMENTED:
 * ⭐ E4: Removed "Draft" label from customer PDFs
 * ⭐ E5: Changed "ARR" to "Annual Subscription"
 * ⭐ E6: Regional date formatting
 * ⭐ E12: Added quote validity/expiry date
 * ⭐ E13: Currency selection support
 * ⭐ E14: Added Grand Total to customer PDF
 * ⭐ M5: Itemization in Customer Quote - List specific modules and connectors
 * ⭐ M7: PDF Itemization - Show user breakdown by type
 * ⭐ M8: Subtotals Structure - Proper section subtotals
 * ⭐ M9: Year-1 vs Annual Recurring View
 * ⭐ M10: Legal Entity on PDF - Region-specific entity with address
 */

// ⭐ M10: Legal Entity Configuration by Region
const legalEntities = {
  US: {
    name: 'Splash Business Intelligence, Inc.',
    address: '3079 Peachtree Industrial Blvd',
    city: 'Duluth, GA 30097',
    country: 'United States',
    registration: 'Delaware Corporation'
  },
  UK: {
    name: 'Splash Business Intelligence UK Ltd',
    address: '71-75 Shelton Street, Covent Garden',
    city: 'London WC2H 9JQ',
    country: 'United Kingdom',
    registration: 'Company No. 12345678'
  },
  Europe: {
    name: 'Splash Business Intelligence UK Ltd',
    address: '71-75 Shelton Street, Covent Garden',
    city: 'London WC2H 9JQ',
    country: 'United Kingdom',
    registration: 'Company No. 12345678'
  },
  MENA: {
    name: 'Splash Business Intelligence ME DMCC',
    address: 'Jumeirah Lake Towers, Cluster T',
    city: 'Dubai',
    country: 'United Arab Emirates',
    registration: 'DMCC License No. DMCC-123456'
  },
  ANZ: {
    name: 'Splash Business Intelligence Pty Ltd',
    address: 'Level 10, 100 Market Street',
    city: 'Sydney NSW 2000',
    country: 'Australia',
    registration: 'ABN 12 345 678 901'
  },
  APAC: {
    name: 'Splash Business Intelligence, Inc.',
    address: '3079 Peachtree Industrial Blvd',
    city: 'Duluth, GA 30097',
    country: 'United States',
    registration: 'Delaware Corporation'
  },
  LATAM: {
    name: 'Splash Business Intelligence, Inc.',
    address: '3079 Peachtree Industrial Blvd',
    city: 'Duluth, GA 30097',
    country: 'United States',
    registration: 'Delaware Corporation'
  }
};

// ⭐ E6 Fix: Date Formatting Utility (Regional date formats)
const formatDate = (dateString, region = 'US') => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  // UK/Europe format: DD Mon YYYY, US format: Mon DD, YYYY
  if (region === 'Europe' || region === 'UK' || region === 'MENA' || region === 'ANZ') {
    return `${day} ${month} ${year}`;
  }
  return `${month} ${day}, ${year}`;
};

// ⭐ E12 Fix: Calculate quote expiry date
const calculateExpiryDate = (createdDate, validityDays = 30, region = 'US') => {
  const date = new Date(createdDate || new Date());
  date.setDate(date.getDate() + validityDays);
  return formatDate(date.toISOString(), region);
};

// Get currency based on region or quote data
const getCurrency = (quote) => {
  if (quote.quote_data?.currency) {
    return quote.quote_data.currency;
  }
  const region = quote.customer_region || quote.quote_data?.region || 'US';
  const regionCurrency = {
    'US': 'USD', 'Europe': 'EUR', 'UK': 'GBP', 'MENA': 'AED', 'ANZ': 'AUD', 'APAC': 'USD', 'LATAM': 'USD'
  };
  return regionCurrency[region] || 'USD';
};

// ⭐ M7: Get user counts by type
const getUserCounts = (quote) => {
  const users = quote.quote_data?.users || {};
  return {
    admin: users.admin || 0,
    readOnly: users.readOnly || 0,
    mobile: users.mobile || 0,
    excel: users.excel || 0,
    sqlConnectEnt: users.sqlConnectEnt || 0,
    glConnect: users.glConnect || 0,
    totalPlatform: (users.admin || 0) + (users.readOnly || 0),
    totalAll: (users.admin || 0) + (users.readOnly || 0) + (users.sqlConnectEnt || 0) + (users.glConnect || 0)
  };
};

// Helper function to calculate PEPM for PDFs
const calculatePEPMForPDF = (quote) => {
  const quoteData = quote.quote_data;
  const employeeCount = quoteData?.employeeCount || 0;
  
  if (employeeCount === 0) return null;
  
  const hasHCMModules = quoteData?.hcmModules && Object.values(quoteData.hcmModules).some(
    modules => Array.isArray(modules) && modules.length > 0
  );
  
  const hcmPipelineNames = [
    'Workday (New)', 'Workday (Legacy)', 'UKG Pro', 'UKG Ready', 'UKG Pro WFM', 'UKG Telestaff',
    'SuccessFactors', 'Taleo Enterprise', 'Taleo Business', 'Cornerstone', 'Ceridian Dayforce',
    'iCIMS', 'Lever'
  ];
  
  const hasHCMPipelines = quoteData?.dataPipelines && Array.isArray(quoteData.dataPipelines) &&
    quoteData.dataPipelines.some(pipe => hcmPipelineNames.includes(pipe));
  
  if (!hasHCMModules && !hasHCMPipelines) return null;
  
  const lineItems = quoteData?.lineItems || [];
  const hcmItems = lineItems.filter(item => {
    const isHCMModule = item.category === 'modules' && item.name.startsWith('HCM:');
    const isHCMPipeline = item.category === 'dataPipelines' &&
      hcmPipelineNames.some(name => item.name.includes(name));
    return isHCMModule || isHCMPipeline;
  });
  
  let hcmARR = 0;
  hcmItems.forEach(item => {
    const itemTotal = item.listPrice * item.quantity;
    const lineDiscount = (quoteData?.lineItemDiscounts?.[item.sku] || 0) / 100;
    const categoryDiscount = (quoteData?.categoryDiscounts?.[item.category] || 0) / 100;
    const effectiveDiscount = lineDiscount > 0 ? lineDiscount : categoryDiscount;
    hcmARR += itemTotal * (1 - effectiveDiscount);
  });
  
  if (hcmARR === 0) return null;
  
  return {
    employeeCount,
    hcmARR: Math.round(hcmARR),
    pepm: (hcmARR / (employeeCount * 12)).toFixed(2),
    hcmItemCount: hcmItems.length
  };
};

// ⭐ M8 & M9: Calculate detailed totals
const calculateDetailedTotals = (quote) => {
  const lineItems = quote.quote_data?.lineItems || [];
  const categoryDiscounts = quote.quote_data?.categoryDiscounts || {};
  const lineItemDiscounts = quote.quote_data?.lineItemDiscounts || {};

  let platformSubtotal = 0, usersSubtotal = 0, connectorsSubtotal = 0;
  let pipelinesSubtotal = 0, modulesSubtotal = 0, servicesSubtotal = 0;
  let productsSubtotal = 0; // All product categories combined
  let totalARR = 0, totalOneTime = 0;

  // Product categories that should be grouped together
  const productCategories = ['reportsEBS', 'reportsCloud', 'financialAnalytics', 'peopleAnalytics', 'glConnect', 'dataPipelines', 'utilities', 'modules'];

  lineItems.forEach(item => {
    const subtotal = item.listPrice * item.quantity;
    const catDisc = categoryDiscounts[item.category] || 0;
    const lineDisc = lineItemDiscounts[item.sku] || 0;
    const disc = (lineDisc > 0 ? lineDisc : catDisc) / 100;
    const finalPrice = subtotal * (1 - disc);

    switch (item.category) {
      case 'platform':
        platformSubtotal += finalPrice;
        break;
      case 'users':
        usersSubtotal += finalPrice;
        break;
      case 'erpConnectors':
        connectorsSubtotal += finalPrice;
        break;
      case 'dataPipelines':
        pipelinesSubtotal += finalPrice;
        productsSubtotal += finalPrice;
        break;
      case 'modules':
        modulesSubtotal += finalPrice;
        productsSubtotal += finalPrice;
        break;
      case 'services':
        servicesSubtotal += finalPrice;
        break;
      default:
        // Catch all other product categories
        if (productCategories.includes(item.category)) {
          productsSubtotal += finalPrice;
        }
        break;
    }

    if (item.isOneTime) {
      totalOneTime += finalPrice;
    } else {
      totalARR += finalPrice;
    }
  });

  return {
    platformSubtotal,
    usersSubtotal,
    connectorsSubtotal,
    pipelinesSubtotal,
    modulesSubtotal,
    productsSubtotal,
    servicesSubtotal,
    totalARR,
    totalOneTime,
    yearOneTotal: totalARR + totalOneTime,
    annualRecurring: totalARR
  };
};

export const generateQuotePDF = async (quote, type = 'internal', viewOnly = true) => {
  const logoBase64 = await loadLogoAsBase64();
  if (type === 'customer') {
    generateCustomerPDF(quote, viewOnly, logoBase64);
  } else {
    generateInternalPDF(quote, viewOnly, logoBase64);
  }
};

// Internal Quote - Detailed with SKUs
const generateInternalPDF = (quote, viewOnly = true, logoBase64 = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  const region = quote.customer_region || quote.quote_data?.region || 'US';
  const currency = getCurrency(quote);
  const validityDays = quote.quote_data?.quoteValidityDays || 30;
  const entity = legalEntities[region] || legalEntities.US;

  // ⭐ M10: Header with Legal Entity and Logo
  let headerTextStartY = 20;
  if (logoBase64) {
    // Logo height 12mm, width auto-calculated based on aspect ratio (800x300 original)
    const logoHeight = 12;
    const logoWidth = logoHeight * (800 / 300);
    doc.addImage(logoBase64, 'PNG', 20, 10, logoWidth, logoHeight);
    headerTextStartY = 24;
  } else {
    doc.setFontSize(24);
    doc.setTextColor(0, 181, 226);
    doc.text('SplashBI', 20, 20);
  }

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(entity.name, 20, headerTextStartY + 2);
  doc.text(entity.address, 20, headerTextStartY + 6);
  doc.text(`${entity.city}, ${entity.country}`, 20, headerTextStartY + 10);

  // Quote Info (right-aligned with page margin)
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text(`Quote #: ${quote.quote_number}`, pageWidth - 20, 15, { align: 'right' });
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Date: ${formatDate(quote.created_at, region)}`, pageWidth - 20, 22, { align: 'right' });
  doc.setFontSize(9);
  doc.text(`Valid Until: ${calculateExpiryDate(quote.created_at, validityDays, region)}`, pageWidth - 20, 29, { align: 'right' });
  doc.setFontSize(8);
  doc.setTextColor(0, 181, 226);
  doc.text('INTERNAL QUOTE', pageWidth - 20, 36, { align: 'right' });

  // Customer Info - Two Column Layout
  let yPos = 50;
  const statusDisplay = quote.status ? (quote.status.charAt(0).toUpperCase() + quote.status.slice(1)) : 'Draft';
  const halfWidth = (pageWidth - 50) / 2;

  // Customer & Company Info Table (Left Column)
  const companyData = [
    ['Customer', quote.customer_name || 'N/A'],
    ['Legal Name', quote.quote_data?.customerLegalName || '-'],
    ['Industry', quote.customer_industry || '-'],
    ['Region', region || 'N/A'],
    ['Currency', currency]
  ];

  // Contact & Deal Info Table (Right Column)
  const contactData = [
    ['Primary Contact', quote.quote_data?.contactName || '-'],
    ['Email', quote.quote_data?.contactEmail || '-'],
    ['Sales Rep', quote.quote_data?.salesRep || '-'],
    ['Opportunity ID', quote.opportunity_id || '-'],
    ['Status', statusDisplay]
  ];

  // Render two tables side by side
  doc.autoTable({
    startY: yPos,
    head: [['Company Information', '']],
    body: companyData,
    theme: 'plain',
    headStyles: { fillColor: [241, 245, 249], textColor: [30, 64, 175], fontSize: 10, fontStyle: 'bold', cellPadding: 3 },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: {
      0: { cellWidth: 28, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: halfWidth - 28, textColor: [30, 30, 30] }
    },
    margin: { left: 20, right: pageWidth - 20 - halfWidth },
    tableWidth: halfWidth
  });

  doc.autoTable({
    startY: yPos,
    head: [['Contact & Deal Info', '']],
    body: contactData,
    theme: 'plain',
    headStyles: { fillColor: [241, 245, 249], textColor: [30, 64, 175], fontSize: 10, fontStyle: 'bold', cellPadding: 3 },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: {
      0: { cellWidth: 28, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: halfWidth - 28, textColor: [30, 30, 30] }
    },
    margin: { left: 20 + halfWidth + 10 },
    tableWidth: halfWidth
  });

  yPos = Math.max(doc.lastAutoTable.finalY, doc.previousAutoTable?.finalY || 0) + 5;

  // Address row (full width if present)
  if (quote.quote_data?.customerAddress) {
    doc.autoTable({
      startY: yPos,
      body: [['Address', quote.quote_data.customerAddress]],
      theme: 'plain',
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 28, fontStyle: 'bold', textColor: [100, 100, 100] },
        1: { textColor: [30, 30, 30] }
      },
      margin: { left: 20, right: 20 }
    });
    yPos = doc.lastAutoTable.finalY + 3;
  }

  // Additional contacts if present (compact row)
  const additionalContacts = [];
  if (quote.quote_data?.billingContactName) {
    additionalContacts.push(`Billing: ${quote.quote_data.billingContactName}${quote.quote_data.billingContactEmail ? ` (${quote.quote_data.billingContactEmail})` : ''}`);
  }
  if (quote.quote_data?.influencerName) {
    additionalContacts.push(`Technical: ${quote.quote_data.influencerName}${quote.quote_data.influencerRole ? ` - ${quote.quote_data.influencerRole}` : ''}`);
  }
  if (additionalContacts.length > 0) {
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('Additional Contacts: ' + additionalContacts.join(' | '), 20, yPos + 3);
    yPos += 8;
  }
  
  // ⭐ M7: User Counts Summary
  const userCounts = getUserCounts(quote);
  if (userCounts.totalPlatform > 0) {
    yPos += 8;
    doc.setFontSize(12);
    doc.setTextColor(0, 181, 226);
    doc.setFont(undefined, 'bold');
    doc.text('User License Breakdown', 20, yPos);
    yPos += 8;
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    
    const userBreakdown = [];
    if (userCounts.admin > 0) userBreakdown.push(`Admin/Dev: ${userCounts.admin}`);
    if (userCounts.readOnly > 0) userBreakdown.push(`Read-Only: ${userCounts.readOnly}`);
    if (userCounts.mobile > 0) userBreakdown.push(`Mobile: ${userCounts.mobile}`);
    if (userCounts.excel > 0) userBreakdown.push(`Excel: ${userCounts.excel}`);
    if (userCounts.sqlConnectEnt > 0) userBreakdown.push(`SQL Connect: ${userCounts.sqlConnectEnt}`);
    if (userCounts.glConnect > 0) userBreakdown.push(`GL Connect: ${userCounts.glConnect}`);
    
    doc.text(userBreakdown.join(' | '), 20, yPos);
    yPos += 6;
    doc.setFont(undefined, 'bold');
    doc.text(`Total Platform Users: ${userCounts.totalPlatform}`, 20, yPos);
    doc.setFont(undefined, 'normal');
  }
  
  // Line Items by Category Groups (matching Final Review UI)
  yPos += 15;
  const lineItems = quote.quote_data?.lineItems || [];

  // Check if HCM deal for PEPM column
  const pepmData = calculatePEPMForPDF(quote);
  const isHCMDeal = !!pepmData;
  const employeeCount = pepmData?.employeeCount || 0;

  // Helper to calculate item final price
  const getItemFinalPrice = (item) => {
    const subtotal = item.listPrice * item.quantity;
    const categoryDiscount = quote.quote_data?.categoryDiscounts?.[item.category] || 0;
    const lineDiscount = quote.quote_data?.lineItemDiscounts?.[item.sku] || 0;
    const effectiveDiscount = lineDiscount > 0 ? lineDiscount : categoryDiscount;
    return subtotal * (1 - effectiveDiscount / 100);
  };

  // Helper to calculate group totals
  const getGroupTotals = (items) => {
    let listTotal = 0, finalTotal = 0;
    items.forEach(item => {
      listTotal += item.listPrice * item.quantity;
      finalTotal += getItemFinalPrice(item);
    });
    return { listTotal, finalTotal, discount: listTotal - finalTotal };
  };

  // Category groups matching Final Review UI
  const categoryGroups = [
    { key: 'platform', label: 'Platform & Deployment', categories: ['platform'], color: [59, 130, 246] },
    { key: 'users', label: 'User Licenses', categories: ['users'], color: [99, 102, 241] },
    { key: 'erpConnectors', label: 'ERP Connectors', categories: ['erpConnectors'], color: [168, 85, 247] },
    {
      key: 'products',
      label: 'Products',
      categories: ['reportsEBS', 'reportsCloud', 'financialAnalytics', 'peopleAnalytics', 'glConnect', 'dataPipelines', 'utilities'],
      color: [20, 184, 166],
      subGroups: [
        { label: 'Reports (EBS & Cloud)', categories: ['reportsEBS', 'reportsCloud'] },
        { label: 'Analytics', categories: ['financialAnalytics', 'peopleAnalytics'] },
        { label: 'Data Pipelines', categories: ['dataPipelines'] },
        { label: 'GL Connect & Utilities', categories: ['glConnect', 'utilities'] }
      ]
    },
    { key: 'services', label: 'Professional Services', categories: ['services'], color: [249, 115, 22] }
  ];

  // Render table for items with optional PEPM column
  const renderItemsTable = (items, groupColor) => {
    if (items.length === 0) return;

    const headers = isHCMDeal
      ? [['SKU', 'Item', 'Qty', 'List', 'Disc %', 'Final', 'Type', 'PEPM']]
      : [['SKU', 'Item', 'Qty', 'List Price', 'Disc %', 'Final Price', 'Type']];

    const tableData = items.map(item => {
      const subtotal = item.listPrice * item.quantity;
      const categoryDiscount = quote.quote_data?.categoryDiscounts?.[item.category] || 0;
      const lineDiscount = quote.quote_data?.lineItemDiscounts?.[item.sku] || 0;
      const effectiveDiscount = lineDiscount > 0 ? lineDiscount : categoryDiscount;
      const finalPrice = subtotal * (1 - effectiveDiscount / 100);
      const itemPepm = (isHCMDeal && !item.isOneTime && employeeCount > 0)
        ? formatCurrency(finalPrice / (employeeCount * 12), currency)
        : '-';

      const row = [
        item.sku,
        item.name,
        item.quantity.toString(),
        formatCurrency(subtotal, currency),
        effectiveDiscount > 0 ? `${effectiveDiscount}%` : '-',
        formatCurrency(finalPrice, currency),
        item.isOneTime ? 'One-Time' : 'Annual'
      ];

      if (isHCMDeal) row.push(itemPepm);
      return row;
    });

    const columnStyles = isHCMDeal ? {
      0: { cellWidth: 24 },
      1: { cellWidth: 48 },
      2: { cellWidth: 10, halign: 'center' },
      3: { cellWidth: 20, halign: 'right' },
      4: { cellWidth: 14, halign: 'center' },
      5: { cellWidth: 20, halign: 'right' },
      6: { cellWidth: 18, halign: 'center', fontSize: 7 },
      7: { cellWidth: 18, halign: 'right', textColor: [168, 85, 247] }
    } : {
      0: { cellWidth: 28 },
      1: { cellWidth: 55 },
      2: { cellWidth: 12, halign: 'center' },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 15, halign: 'center' },
      5: { cellWidth: 22, halign: 'right' },
      6: { cellWidth: 20, halign: 'center', fontSize: 7 }
    };

    doc.autoTable({
      startY: yPos,
      head: headers,
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: groupColor, textColor: 255, fontSize: 8 },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles
    });

    yPos = doc.lastAutoTable.finalY;
  };

  // Render subtotal row
  const renderSubtotal = (label, totals) => {
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text(label, 25, yPos + 5);
    doc.text(`List: ${formatCurrency(totals.listTotal, currency)}`, pageWidth - 95, yPos + 5, { align: 'right' });
    if (totals.discount > 0) {
      doc.setTextColor(220, 38, 38);
      doc.text(`-${formatCurrency(totals.discount, currency)}`, pageWidth - 60, yPos + 5, { align: 'right' });
    }
    doc.setTextColor(22, 163, 74);
    doc.text(formatCurrency(totals.finalTotal, currency), pageWidth - 25, yPos + 5, { align: 'right' });
    doc.setFont(undefined, 'normal');
    yPos += 10;
  };

  // Render each category group
  categoryGroups.forEach(group => {
    const groupItems = lineItems.filter(item => group.categories.includes(item.category));
    if (groupItems.length === 0) return;

    // Check for page break
    if (yPos > doc.internal.pageSize.getHeight() - 60) {
      doc.addPage();
      yPos = 30;
    }

    // Group header
    doc.setFillColor(...group.color);
    doc.rect(20, yPos, pageWidth - 40, 8, 'F');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text(group.label, 25, yPos + 6);

    const groupTotals = getGroupTotals(groupItems);
    doc.text(`${formatCurrency(groupTotals.finalTotal, currency)}`, pageWidth - 25, yPos + 6, { align: 'right' });
    if (groupTotals.discount > 0) {
      doc.setFontSize(8);
      doc.text(`(-${formatCurrency(groupTotals.discount, currency)} disc)`, pageWidth - 55, yPos + 6, { align: 'right' });
    }
    doc.setFont(undefined, 'normal');
    yPos += 12;

    // If group has sub-groups, render each sub-group
    if (group.subGroups) {
      group.subGroups.forEach(subGroup => {
        const subItems = lineItems.filter(item => subGroup.categories.includes(item.category));
        if (subItems.length === 0) return;

        // Sub-group header
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont(undefined, 'bold');
        doc.text(`- ${subGroup.label}`, 22, yPos + 4);
        doc.setFont(undefined, 'normal');
        yPos += 8;

        renderItemsTable(subItems, group.color);

        const subTotals = getGroupTotals(subItems);
        renderSubtotal(`${subGroup.label} Subtotal:`, subTotals);
        yPos += 2;
      });
    } else {
      // Render items directly
      renderItemsTable(groupItems, group.color);
      yPos += 8;
    }
  });
  
  // ⭐ M8 & M9: Enhanced Totals Section
  const detailedTotals = calculateDetailedTotals(quote);
  
  if (yPos > doc.internal.pageSize.getHeight() - 100) {
    doc.addPage();
    yPos = 30;
  }
  
  doc.setDrawColor(0, 181, 226);
  doc.setLineWidth(1);
  doc.line(20, yPos, pageWidth - 20, yPos);
  yPos += 15;
  
  doc.setFontSize(14);
  doc.setTextColor(0, 181, 226);
  doc.setFont(undefined, 'bold');
  doc.text('Investment Summary', 20, yPos);
  yPos += 12;
  
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.setFont(undefined, 'normal');
  
  // Section Subtotals
  const subtotalLines = [
    ['Platform & Infrastructure:', detailedTotals.platformSubtotal],
    ['User Licenses:', detailedTotals.usersSubtotal],
    ['ERP Connectors:', detailedTotals.connectorsSubtotal],
    ['Data Pipelines:', detailedTotals.pipelinesSubtotal],
    ['Business Modules:', detailedTotals.modulesSubtotal]
  ].filter(([_, val]) => val > 0);
  
  subtotalLines.forEach(([label, value]) => {
    doc.text(label, 25, yPos);
    doc.text(formatCurrency(value, currency), pageWidth - 25, yPos, { align: 'right' });
    yPos += 7;
  });
  
  yPos += 5;
  doc.setDrawColor(200, 200, 200);
  doc.line(pageWidth - 100, yPos, pageWidth - 25, yPos);
  yPos += 10;
  
  // Annual Recurring
  doc.setFont(undefined, 'bold');
  doc.text('Annual Subscription:', 25, yPos);
  doc.setTextColor(22, 163, 74);
  doc.text(formatCurrency(detailedTotals.annualRecurring, currency), pageWidth - 25, yPos, { align: 'right' });
  yPos += 10;
  
  // One-Time Services
  if (detailedTotals.servicesSubtotal > 0) {
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    doc.text('Professional Services (One-Time):', 25, yPos);
    doc.setTextColor(0, 181, 226);
    doc.text(formatCurrency(detailedTotals.servicesSubtotal, currency), pageWidth - 25, yPos, { align: 'right' });
    yPos += 10;
  }
  
  // ⭐ M9: Year-1 vs Annual distinction
  yPos += 5;
  doc.setDrawColor(0, 181, 226);
  doc.setLineWidth(1);
  doc.line(pageWidth - 100, yPos, pageWidth - 25, yPos);
  yPos += 12;
  
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text('YEAR 1 TOTAL:', 25, yPos);
  doc.setTextColor(22, 163, 74);
  doc.setFontSize(14);
  doc.text(formatCurrency(detailedTotals.yearOneTotal, currency), pageWidth - 25, yPos, { align: 'right' });
  yPos += 8;
  
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont(undefined, 'normal');
  doc.text(`(Annual Recurring: ${formatCurrency(detailedTotals.annualRecurring, currency)}/year after Year 1)`, pageWidth - 25, yPos, { align: 'right' });
  
  // PEPM Analysis (pepmData already calculated above)
  if (pepmData) {
    yPos += 25;
    if (yPos > doc.internal.pageSize.getHeight() - 80) {
      doc.addPage();
      yPos = 30;
    }
    
    doc.setFontSize(14);
    doc.setTextColor(147, 51, 234);
    doc.setFont(undefined, 'bold');
    doc.text('HCM PEPM Analysis', 20, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    doc.text(`Employee Count: ${pepmData.employeeCount.toLocaleString()}`, 25, yPos);
    yPos += 6;
    doc.text(`HCM Annual: ${formatCurrency(pepmData.hcmARR, currency)}`, 25, yPos);
    yPos += 6;
    doc.setFont(undefined, 'bold');
    doc.setTextColor(147, 51, 234);
    doc.text(`PEPM: ${formatCurrency(parseFloat(pepmData.pepm), currency)}/employee/month`, 25, yPos);
  }
  
  // Terms & Conditions Page
  if (quote.quote_data?.terms) {
    doc.addPage();
    yPos = 30;
    
    doc.setFontSize(16);
    doc.setTextColor(0, 181, 226);
    doc.text('Terms & Conditions', 20, yPos);
    yPos += 15;
    
    const terms = quote.quote_data.terms;
    const actualDeployment = quote.quote_data?.deployment || terms.deploymentType;
    
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    
    // Contract Terms
    doc.setFont(undefined, 'bold');
    doc.text('Contract Terms', 20, yPos);
    yPos += 7;
    doc.setFont(undefined, 'normal');
    doc.text(`Contract Term: ${terms.contractTerm || 'N/A'}`, 25, yPos);
    yPos += 6;
    doc.text(`Start Date: ${terms.startDate ? formatDate(terms.startDate, region) : 'TBD'}`, 25, yPos);
    yPos += 6;
    if (terms.contractTerm === 'Custom' && terms.endDate) {
      doc.text(`End Date: ${formatDate(terms.endDate, region)}`, 25, yPos);
      yPos += 6;
    }
    // ⭐ M16: Enhanced payment terms
    const paymentTermsDisplay = terms.paymentTerms === 'Custom' && terms.customPaymentTerms 
      ? terms.customPaymentTerms 
      : terms.paymentTerms || 'N/A';
    doc.text(`Payment Terms: ${paymentTermsDisplay}`, 25, yPos);
    yPos += 6;
    if (terms.paymentMethod) {
      doc.text(`Payment Method: ${terms.paymentMethod}`, 25, yPos);
      yPos += 6;
    }
    yPos += 6;
    
    // Deployment
    doc.setFont(undefined, 'bold');
    doc.text('Deployment & Infrastructure', 20, yPos);
    yPos += 7;
    doc.setFont(undefined, 'normal');
    doc.text(`Type: ${actualDeployment || 'N/A'}`, 25, yPos);
    yPos += 6;
    doc.text(`Instances: ${terms.instances || 'N/A'}`, 25, yPos);
    yPos += 12;
    
    // Professional Services
    doc.setFont(undefined, 'bold');
    doc.text('Professional Services', 20, yPos);
    yPos += 7;
    doc.setFont(undefined, 'normal');
    doc.text(`Implementation: ${terms.implementation || 'N/A'}`, 25, yPos);
    yPos += 6;
    doc.text(`Training: ${terms.training || 'N/A'}`, 25, yPos);
    yPos += 6;
    doc.text(`Onshore Rate: ${terms.ratesOnshore || 'N/A'}`, 25, yPos);
    yPos += 6;
    doc.text(`Offshore Rate: ${terms.ratesOffshore || 'N/A'}`, 25, yPos);
    yPos += 12;
    
    // Support
    doc.setFont(undefined, 'bold');
    doc.text('Support', 20, yPos);
    yPos += 7;
    doc.setFont(undefined, 'normal');
    doc.text(`Authorized Users: ${terms.supportUsers || 'N/A'}`, 25, yPos);
    yPos += 6;
    doc.text(`Timing: ${terms.supportTiming || 'N/A'}`, 25, yPos);
  }
  
  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 15;
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('Internal Use Only - Contains Provisioning SKUs', pageWidth / 2, footerY, { align: 'center' });

  // View in new tab or download
  if (viewOnly) {
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  } else {
    doc.save(`${quote.quote_number}_Internal_Detailed.pdf`);
  }
};

/**
 * Customer PDF - Based on Internal PDF structure
 * Same professional layout, but customer-friendly:
 * - No SKUs
 * - No discount percentages
 * - No list prices (only final prices)
 * - Clean item names
 * - Header repeated on all pages
 * - Footer with confidentiality and page numbers
 */
const generateCustomerPDF = (quote, viewOnly = true, logoBase64 = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const region = quote.customer_region || quote.quote_data?.region || 'US';
  const currency = getCurrency(quote);
  const validityDays = quote.quote_data?.quoteValidityDays || 30;
  const entity = legalEntities[region] || legalEntities.US;

  // Page layout constants
  const headerHeight = 45;
  const footerHeight = 15; // Reserved space for footer
  const contentStartY = headerHeight + 5;
  const contentEndY = pageHeight - footerHeight;

  // Function to draw header on any page
  const drawHeader = () => {
    let headerTextStartY = 20;
    if (logoBase64) {
      const logoH = 12;
      const logoW = logoH * (800 / 300);
      doc.addImage(logoBase64, 'PNG', 20, 10, logoW, logoH);
      headerTextStartY = 24;
    } else {
      doc.setFontSize(24);
      doc.setTextColor(0, 181, 226);
      doc.setFont(undefined, 'bold');
      doc.text('SplashBI', 20, 20);
    }

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text(entity.name, 20, headerTextStartY + 2);
    doc.text(entity.address, 20, headerTextStartY + 6);
    doc.text(`${entity.city}, ${entity.country}`, 20, headerTextStartY + 10);

    // Quote Info (right-aligned)
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text(`Quote #: ${quote.quote_number}`, pageWidth - 20, 15, { align: 'right' });
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text(`Date: ${formatDate(quote.created_at, region)}`, pageWidth - 20, 22, { align: 'right' });
    doc.text(`Valid Until: ${calculateExpiryDate(quote.created_at, validityDays, region)}`, pageWidth - 20, 29, { align: 'right' });

    // Header line
    doc.setDrawColor(0, 181, 226);
    doc.setLineWidth(0.5);
    doc.line(20, headerHeight, pageWidth - 20, headerHeight);
  };

  // Function to draw footer on any page
  const drawFooter = (pageNum, totalPages) => {
    const footerY = pageHeight - 12;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont(undefined, 'normal');
    doc.text(`© ${new Date().getFullYear()} SplashBI Confidential`, 20, footerY);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 20, footerY, { align: 'right' });
  };

  // Draw header on first page
  drawHeader();

  // === CUSTOMER INFO - Two Column Layout ===
  let yPos = contentStartY;

  // Draw section headers manually (not in table) to avoid wrapping
  doc.setFillColor(241, 245, 249);
  doc.rect(20, yPos, 80, 7, 'F');
  doc.rect(105, yPos, 85, 7, 'F');
  doc.setFontSize(10);
  doc.setTextColor(30, 64, 175);
  doc.setFont(undefined, 'bold');
  doc.text('Company Information', 25, yPos + 5);
  doc.text('Contact Information', 110, yPos + 5);
  yPos += 10;

  // Company data (left column)
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  const companyRows = [
    ['Customer', quote.quote_data?.customerLegalName || quote.customer_name || 'N/A'],
    ['Industry', quote.customer_industry || '-'],
    ['Region', region || 'N/A'],
    ['Currency', currency]
  ];
  companyRows.forEach(([label, value]) => {
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'bold');
    doc.text(label, 25, yPos);
    doc.setTextColor(30, 30, 30);
    doc.setFont(undefined, 'normal');
    doc.text(value, 50, yPos);
    yPos += 5;
  });

  // Contact data (right column) - reset yPos for right column
  let rightYPos = contentStartY + 10;
  const contactRows = [
    ['Contact', quote.quote_data?.contactName || '-'],
    ['Email', quote.quote_data?.contactEmail || '-'],
    ['Sales Rep', quote.quote_data?.salesRep || '-']
  ];
  contactRows.forEach(([label, value]) => {
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'bold');
    doc.text(label, 110, rightYPos);
    doc.setTextColor(30, 30, 30);
    doc.setFont(undefined, 'normal');
    doc.text(String(value).substring(0, 35), 135, rightYPos); // Truncate long emails
    rightYPos += 5;
  });

  yPos = Math.max(yPos, rightYPos) + 5;

  // === USER LICENSE BREAKDOWN (same as internal) ===
  const userCounts = getUserCounts(quote);
  if (userCounts.totalPlatform > 0) {
    yPos += 8;
    doc.setFontSize(12);
    doc.setTextColor(0, 181, 226);
    doc.setFont(undefined, 'bold');
    doc.text('User License Breakdown', 20, yPos);
    yPos += 8;
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');

    const userBreakdown = [];
    if (userCounts.admin > 0) userBreakdown.push(`Admin/Dev: ${userCounts.admin}`);
    if (userCounts.readOnly > 0) userBreakdown.push(`Read-Only: ${userCounts.readOnly}`);
    if (userCounts.mobile > 0) userBreakdown.push(`Mobile: ${userCounts.mobile}`);
    if (userCounts.excel > 0) userBreakdown.push(`Excel: ${userCounts.excel}`);
    if (userCounts.sqlConnectEnt > 0) userBreakdown.push(`SQL Connect: ${userCounts.sqlConnectEnt}`);
    if (userCounts.glConnect > 0) userBreakdown.push(`GL Connect: ${userCounts.glConnect}`);

    doc.text(userBreakdown.join(' | '), 20, yPos);
    yPos += 6;
    doc.setFont(undefined, 'bold');
    doc.text(`Total Platform Users: ${userCounts.totalPlatform}`, 20, yPos);
    doc.setFont(undefined, 'normal');
  }

  // === LINE ITEMS BY CATEGORY (same structure as internal, but NO SKUs, NO discounts) ===
  yPos += 15;
  const lineItems = quote.quote_data?.lineItems || [];

  // Helper to get final price for an item
  const getItemFinalPrice = (item) => {
    const subtotal = item.listPrice * item.quantity;
    const categoryDiscount = quote.quote_data?.categoryDiscounts?.[item.category] || 0;
    const lineDiscount = quote.quote_data?.lineItemDiscounts?.[item.sku] || 0;
    const effectiveDiscount = lineDiscount > 0 ? lineDiscount : categoryDiscount;
    return subtotal * (1 - effectiveDiscount / 100);
  };

  // Helper to get group totals
  const getGroupTotals = (items) => {
    let finalTotal = 0;
    items.forEach(item => {
      finalTotal += getItemFinalPrice(item);
    });
    return { finalTotal };
  };

  // Clean item name for customer display
  const cleanItemName = (name) => {
    let clean = name;
    if (clean.startsWith('HCM: ')) clean = clean.substring(5);
    if (clean.startsWith('EBS: ')) clean = clean.substring(5);
    if (clean.startsWith('Cloud: ')) clean = clean.substring(7);
    return clean;
  };

  // Helper: check if item is an admin user license
  const isAdminLicense = (item) => item.category === 'users' && item.sku === 'BE:USER_ADMIN_DEV';

  // 4 sections: Platform & Infrastructure, User Licenses, Products, Services
  const categoryGroups = [
    { key: 'platform', label: 'Platform and Infrastructure', categories: ['platform', 'erpConnectors'], includeAdmin: true, color: [59, 130, 246] },
    { key: 'users', label: 'User Licenses', categories: ['users'], excludeAdmin: true, color: [99, 102, 241] },
    {
      key: 'products',
      label: 'Products',
      categories: ['reportsEBS', 'reportsCloud', 'financialAnalytics', 'peopleAnalytics', 'glConnect', 'crm', 'dataPipelines', 'utilities'],
      color: [20, 184, 166],
      subGroups: [
        { label: 'Analytics', categories: ['reportsEBS', 'reportsCloud', 'financialAnalytics', 'peopleAnalytics', 'glConnect', 'crm', 'utilities'] },
        { label: 'Data Pipelines', categories: ['dataPipelines'] }
      ]
    },
    { key: 'services', label: 'Services', categories: ['services'], color: [249, 115, 22] }
  ];

  // Render customer-friendly table: Item, Qty, Type — crisp 3-column layout
  const renderCustomerItemsTable = (items, groupColor) => {
    if (items.length === 0) return;

    const tableData = items.map(item => [
      cleanItemName(item.name),
      item.quantity.toString(),
      item.isOneTime ? 'One-Time' : 'Annual'
    ]);

    // Column widths: 118 + 26 + 26 = 170mm (pageWidth - 40 margins)
    doc.autoTable({
      startY: yPos,
      head: [['Item', 'Qty', 'Type']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: groupColor, textColor: 255, fontSize: 8, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 118 },
        1: { cellWidth: 26, halign: 'center' },
        2: { cellWidth: 26, halign: 'center' }
      },
      margin: { left: 20, right: 20, bottom: footerHeight },
      tableWidth: pageWidth - 40,
      alternateRowStyles: { fillColor: [250, 250, 250] },
      // Draw header on new pages created by table overflow
      didDrawPage: (data) => {
        if (data.pageNumber > 1 || data.pageCount > 1) {
          drawHeader();
        }
      }
    });

    yPos = doc.lastAutoTable.finalY;
  };

  // Render subtotal row (with page break check)
  const renderSubtotal = (label, total) => {
    // Ensure we have space for the subtotal line
    if (yPos + 15 > contentEndY) {
      doc.addPage();
      drawHeader();
      yPos = contentStartY;
    }
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text(label, 25, yPos + 5);
    doc.setTextColor(22, 163, 74);
    doc.text(formatCurrency(total, currency), pageWidth - 25, yPos + 5, { align: 'right' });
    doc.setFont(undefined, 'normal');
    yPos += 10;
  };

  // Helper to check page break and add header on new pages
  // Ensures content stays above footer area (contentEndY)
  const checkPageBreak = (neededSpace = 60) => {
    if (yPos + neededSpace > contentEndY) {
      doc.addPage();
      drawHeader();
      yPos = contentStartY;
    }
  };

  // Render each category group
  categoryGroups.forEach(group => {
    let groupItems = lineItems.filter(item => group.categories.includes(item.category));
    // Platform & Infrastructure also includes admin user licenses
    if (group.includeAdmin) {
      groupItems = groupItems.concat(lineItems.filter(isAdminLicense));
    }
    // User Licenses excludes admin licenses (they're in Platform)
    if (group.excludeAdmin) {
      groupItems = groupItems.filter(item => !isAdminLicense(item));
    }
    if (groupItems.length === 0) return;

    checkPageBreak(60);

    // Group header
    doc.setFillColor(...group.color);
    doc.rect(20, yPos, pageWidth - 40, 8, 'F');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text(group.label, 25, yPos + 6);

    const groupTotals = getGroupTotals(groupItems);
    doc.text(`${formatCurrency(groupTotals.finalTotal, currency)}`, pageWidth - 25, yPos + 6, { align: 'right' });
    doc.setFont(undefined, 'normal');
    yPos += 12;

    // If group has sub-groups, render each
    if (group.subGroups) {
      group.subGroups.forEach(subGroup => {
        const subItems = lineItems.filter(item => subGroup.categories.includes(item.category));
        if (subItems.length === 0) return;

        checkPageBreak(40);

        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont(undefined, 'bold');
        doc.text(`- ${subGroup.label}`, 22, yPos + 4);
        doc.setFont(undefined, 'normal');
        yPos += 8;

        renderCustomerItemsTable(subItems, group.color);

        const subTotals = getGroupTotals(subItems);
        renderSubtotal(`${subGroup.label} Subtotal:`, subTotals.finalTotal);
        yPos += 4;
      });
    } else {
      renderCustomerItemsTable(groupItems, group.color);
      yPos += 8;
    }
  });

  // === INVESTMENT SUMMARY ===
  const detailedTotals = calculateDetailedTotals(quote);

  // Parse contract term to get number of years
  const contractTerm = quote.quote_data?.terms?.contractTerm || '1 Year';
  let contractYears = 1;
  if (contractTerm.includes('2')) contractYears = 2;
  else if (contractTerm.includes('3')) contractYears = 3;
  else if (contractTerm === 'Custom' && quote.quote_data?.terms?.startDate && quote.quote_data?.terms?.endDate) {
    const start = new Date(quote.quote_data.terms.startDate);
    const end = new Date(quote.quote_data.terms.endDate);
    contractYears = Math.max(1, Math.round((end - start) / (365 * 24 * 60 * 60 * 1000)));
  }

  checkPageBreak(120);

  doc.setDrawColor(0, 181, 226);
  doc.setLineWidth(1);
  doc.line(20, yPos, pageWidth - 20, yPos);
  yPos += 15;

  doc.setFontSize(14);
  doc.setTextColor(0, 181, 226);
  doc.setFont(undefined, 'bold');
  doc.text('Investment Summary', 20, yPos);
  yPos += 12;

  // --- Annual Recurring Revenue Breakdown ---
  doc.setFontSize(10);
  doc.setTextColor(0, 181, 226);
  doc.setFont(undefined, 'bold');
  doc.text('Annual Recurring Revenue (ARR)', 25, yPos);
  yPos += 8;

  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.setFont(undefined, 'normal');

  // Calculate admin user subtotal for proper grouping
  const adminUserTotal = lineItems
    .filter(item => item.category === 'users' && item.sku === 'BE:USER_ADMIN_DEV')
    .reduce((sum, item) => sum + getItemFinalPrice(item), 0);

  // All recurring components that make up ARR (4 sections)
  const arrComponents = [
    ['Platform & Infrastructure', detailedTotals.platformSubtotal + detailedTotals.connectorsSubtotal + adminUserTotal],
    ['User Licenses', detailedTotals.usersSubtotal - adminUserTotal],
    ['Products', detailedTotals.productsSubtotal]
  ].filter(([, val]) => val > 0);

  arrComponents.forEach(([label, value]) => {
    doc.text(label, 30, yPos);
    doc.text(formatCurrency(value, currency), pageWidth - 25, yPos, { align: 'right' });
    yPos += 6;
  });

  // ARR Subtotal line
  yPos += 3;
  doc.setDrawColor(200, 200, 200);
  doc.line(25, yPos, pageWidth - 25, yPos);
  yPos += 8;

  doc.setFont(undefined, 'bold');
  doc.setTextColor(22, 163, 74);
  doc.text('Total ARR:', 30, yPos);
  doc.text(formatCurrency(detailedTotals.annualRecurring, currency), pageWidth - 25, yPos, { align: 'right' });
  yPos += 12;

  // --- One-Time Fees ---
  if (detailedTotals.servicesSubtotal > 0) {
    doc.setFontSize(10);
    doc.setTextColor(0, 181, 226);
    doc.setFont(undefined, 'bold');
    doc.text('One-Time Fees', 25, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    doc.text('Professional Services', 30, yPos);
    doc.text(formatCurrency(detailedTotals.servicesSubtotal, currency), pageWidth - 25, yPos, { align: 'right' });
    yPos += 10;
  }

  // --- Contract Summary ---
  yPos += 5;
  doc.setDrawColor(0, 181, 226);
  doc.setLineWidth(1.5);
  doc.line(20, yPos, pageWidth - 20, yPos);
  yPos += 12;

  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text(`Contract Term: ${contractTerm}`, 25, yPos);
  yPos += 10;

  // Year 1 Total
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.setFont(undefined, 'normal');
  doc.text('Year 1 Investment:', 25, yPos);
  doc.text(formatCurrency(detailedTotals.yearOneTotal, currency), pageWidth - 25, yPos, { align: 'right' });
  yPos += 7;

  // Show multi-year breakdown if contract > 1 year
  if (contractYears > 1) {
    doc.text(`Years 2-${contractYears} (ARR x ${contractYears - 1}):`, 25, yPos);
    doc.text(formatCurrency(detailedTotals.annualRecurring * (contractYears - 1), currency), pageWidth - 25, yPos, { align: 'right' });
    yPos += 10;

    // Total Contract Value
    const totalContractValue = (detailedTotals.annualRecurring * contractYears) + detailedTotals.servicesSubtotal;

    doc.setDrawColor(22, 163, 74);
    doc.setLineWidth(1);
    doc.line(pageWidth - 100, yPos, pageWidth - 25, yPos);
    yPos += 10;

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text(`TOTAL ${contractYears}-YEAR CONTRACT:`, 25, yPos);
    doc.setTextColor(22, 163, 74);
    doc.setFontSize(14);
    doc.text(formatCurrency(totalContractValue, currency), pageWidth - 25, yPos, { align: 'right' });
    yPos += 8;

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text(`(${formatCurrency(detailedTotals.annualRecurring, currency)}/yr x ${contractYears} years + ${formatCurrency(detailedTotals.servicesSubtotal, currency)} one-time)`, pageWidth - 25, yPos, { align: 'right' });
  } else {
    // Single year - just show Year 1 total prominently
    yPos += 3;
    doc.setDrawColor(22, 163, 74);
    doc.setLineWidth(1);
    doc.line(pageWidth - 100, yPos, pageWidth - 25, yPos);
    yPos += 10;

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text('TOTAL INVESTMENT:', 25, yPos);
    doc.setTextColor(22, 163, 74);
    doc.setFontSize(14);
    doc.text(formatCurrency(detailedTotals.yearOneTotal, currency), pageWidth - 25, yPos, { align: 'right' });
    yPos += 8;

    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text(`(Annual Recurring: ${formatCurrency(detailedTotals.annualRecurring, currency)}/year)`, pageWidth - 25, yPos, { align: 'right' });
  }

  // === PEPM for HCM deals ===
  const pepmData = calculatePEPMForPDF(quote);
  if (pepmData) {
    yPos += 25;
    checkPageBreak(80);

    doc.setFontSize(14);
    doc.setTextColor(147, 51, 234);
    doc.setFont(undefined, 'bold');
    doc.text('HCM PEPM Analysis', 20, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    doc.text(`Employee Count: ${pepmData.employeeCount.toLocaleString()}`, 25, yPos);
    yPos += 6;
    doc.text(`HCM Annual: ${formatCurrency(pepmData.hcmARR, currency)}`, 25, yPos);
    yPos += 6;
    doc.setFont(undefined, 'bold');
    doc.setTextColor(147, 51, 234);
    doc.text(`PEPM: ${formatCurrency(parseFloat(pepmData.pepm), currency)}/employee/month`, 25, yPos);
  }

  // === KEY TERMS ===
  if (quote.quote_data?.terms) {
    const terms = quote.quote_data.terms;

    yPos += 20;
    checkPageBreak(70);

    doc.setFontSize(12);
    doc.setTextColor(0, 181, 226);
    doc.setFont(undefined, 'bold');
    doc.text('Terms & Conditions', 20, yPos);
    yPos += 10;

    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');

    const actualDeployment = quote.quote_data?.deployment || terms.deploymentType;
    const paymentTermsDisplay = terms.paymentTerms === 'Custom' && terms.customPaymentTerms
      ? terms.customPaymentTerms
      : terms.paymentTerms || 'N/A';

    doc.text(`Contract Term: ${terms.contractTerm || 'N/A'}`, 25, yPos);
    yPos += 6;
    doc.text(`Deployment: ${actualDeployment || 'N/A'}`, 25, yPos);
    yPos += 6;
    doc.text(`Payment Terms: ${paymentTermsDisplay}`, 25, yPos);
    yPos += 6;
    if (terms.startDate) {
      doc.text(`Start Date: ${formatDate(terms.startDate, region)}`, 25, yPos);
    }
  }

  // === ADD FOOTER TO ALL PAGES ===
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(i, totalPages);
  }

  // View in new tab or download
  if (viewOnly) {
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  } else {
    doc.save(`${quote.quote_number}_Customer_${quote.customer_name?.replace(/[^a-z0-9]/gi, '_') || 'Quote'}.pdf`);
  }
};

/**
 * External Quote PDF Generator
 * Generates a professional customer-facing PDF from the rich JSON quote structure
 * No line item pricing - shows itemized components with category subtotals only
 */
export const generateExternalQuotePDF = async (quoteJson, viewOnly = true) => {
  const logoBase64 = await loadLogoAsBase64();
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const q = quoteJson.quote;
  const meta = q.metadata;
  const customer = q.customer;
  const vendor = q.vendor;
  const salesRep = q.salesRep;
  const pricing = q.pricing;
  const terms = q.terms;
  const currency = meta.currency || 'USD';

  // Currency formatter
  const fmt = (amount) => {
    const symbols = { USD: '$', GBP: '£', EUR: '€', AED: 'AED ', AUD: 'A$', CAD: 'C$' };
    const symbol = symbols[currency] || '$';
    return `${symbol}${Math.round(amount).toLocaleString()}`;
  };

  // Date formatter
  const fmtDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  // === PAGE 1: HEADER ===
  let yPos = 15;

  // Logo
  if (logoBase64) {
    const logoHeight = 14;
    const logoWidth = logoHeight * (800 / 300);
    doc.addImage(logoBase64, 'PNG', 20, 10, logoWidth, logoHeight);
    yPos = 26;
  } else {
    doc.setFontSize(24);
    doc.setTextColor(0, 181, 226);
    doc.setFont(undefined, 'bold');
    doc.text('SplashBI', 20, 22);
    yPos = 26;
  }

  // Vendor info (below logo)
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.setFont(undefined, 'normal');
  doc.text(vendor.address.street, 20, yPos);
  doc.text(`${vendor.address.city}, ${vendor.address.state} ${vendor.address.zip}`, 20, yPos + 4);

  // Quote info (right aligned)
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text(`Quote: ${meta.quoteId}`, pageWidth - 20, 15, { align: 'right' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont(undefined, 'normal');
  doc.text(`Date: ${fmtDate(meta.createdDate)}`, pageWidth - 20, 22, { align: 'right' });
  doc.text(`Valid Until: ${fmtDate(meta.expirationDate)}`, pageWidth - 20, 28, { align: 'right' });

  // Divider
  yPos = 42;
  doc.setDrawColor(0, 181, 226);
  doc.setLineWidth(0.5);
  doc.line(20, yPos, pageWidth - 20, yPos);

  // === PREPARED FOR ===
  yPos += 12;
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text('PREPARED FOR', 20, yPos);
  yPos += 8;

  doc.setFontSize(16);
  doc.setTextColor(30, 30, 30);
  doc.setFont(undefined, 'bold');
  doc.text(customer.companyName, 20, yPos);
  yPos += 8;

  if (customer.contact?.name) {
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.setFont(undefined, 'normal');
    const contactLine = customer.contact.name + (customer.contact.email ? ` • ${customer.contact.email}` : '');
    doc.text(contactLine, 20, yPos);
    yPos += 6;
  }

  // === EXECUTIVE SUMMARY BOX ===
  if (q.executiveSummary) {
    yPos += 8;
    const summaryBoxTop = yPos;

    // Light blue background
    doc.setFillColor(240, 249, 255);
    doc.setDrawColor(0, 181, 226);
    doc.setLineWidth(0.3);
    doc.roundedRect(20, summaryBoxTop, pageWidth - 40, 48, 3, 3, 'FD');

    yPos = summaryBoxTop + 10;
    doc.setFontSize(11);
    doc.setTextColor(0, 181, 226);
    doc.setFont(undefined, 'bold');
    doc.text(q.executiveSummary.headline, 28, yPos);
    yPos += 10;

    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    doc.text(q.executiveSummary.proposedSolution, 28, yPos);
    yPos += 10;

    // Key benefits (first 3)
    const benefits = q.executiveSummary.keyBenefits?.slice(0, 3) || [];
    benefits.forEach((benefit, i) => {
      doc.text(`✓ ${benefit}`, 32, yPos + (i * 6));
    });

    yPos = summaryBoxTop + 55;
  }

  // === SOLUTION COMPONENTS ===
  yPos += 8;
  doc.setFontSize(12);
  doc.setTextColor(0, 181, 226);
  doc.setFont(undefined, 'bold');
  doc.text('Solution Overview', 20, yPos);
  yPos += 10;

  const groupColors = {
    platform: [59, 130, 246],
    licenses: [99, 102, 241],
    products: [20, 184, 166],
    services: [249, 115, 22]
  };

  // Column widths: Item + Qty + Type = pageWidth - 40 (170mm)
  const colWidths = { item: 118, qty: 26, type: 26 };

  // Helper: render items table with Item/Qty/Type headers
  const renderSectionTable = (items, color) => {
    if (!items || items.length === 0) return;
    const displayItems = items.filter(item => item.finalPrice > 0 || item.includedWith);
    if (displayItems.length === 0) return;

    const tableData = displayItems.map(item => [
      item.name + (item.includedWith ? ' (Included)' : ''),
      String(item.quantity || 1),
      item.billingFrequency === 'one-time' ? 'One-Time' : 'Annual'
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Item', 'Qty', 'Type']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: color, textColor: 255, fontSize: 8, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: colWidths.item },
        1: { cellWidth: colWidths.qty, halign: 'center' },
        2: { cellWidth: colWidths.type, halign: 'center' }
      },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      margin: { left: 20, right: 20 }
    });
    yPos = doc.lastAutoTable.finalY;
  };

  // Render each section
  (q.lineItemGroups || []).forEach(group => {
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = 25;
    }

    const color = groupColors[group.groupId] || [100, 100, 100];

    // Section header bar
    doc.setFillColor(...color);
    doc.rect(20, yPos, pageWidth - 40, 8, 'F');
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text(group.groupName, 25, yPos + 5.5);
    doc.setFont(undefined, 'normal');
    yPos += 11;

    // Products section: render sub-categories with subtotals
    if (group.categories && group.categories.length > 0) {
      group.categories.forEach(cat => {
        if (!cat.items || cat.items.length === 0) return;
        if (yPos > pageHeight - 50) {
          doc.addPage();
          yPos = 25;
        }

        // Sub-category label
        doc.setFontSize(9);
        doc.setTextColor(...color);
        doc.setFont(undefined, 'bold');
        doc.text(cat.categoryName, 24, yPos + 3);
        doc.setFont(undefined, 'normal');
        yPos += 7;

        renderSectionTable(cat.items, color);

        // Sub-category subtotal
        if (cat.subtotal > 0) {
          yPos += 3;
          doc.setFontSize(8);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(80, 80, 80);
          doc.text(`${cat.categoryName} Subtotal:`, 28, yPos + 3);
          doc.setTextColor(22, 163, 74);
          doc.text(fmt(cat.subtotal), pageWidth - 25, yPos + 3, { align: 'right' });
          doc.setFont(undefined, 'normal');
          yPos += 9;
        }
      });
      yPos += 4;
    } else {
      // Direct items (Platform & Infrastructure, User Licenses, Services)
      renderSectionTable(group.items || [], color);
      yPos += 6;
    }
  });

  // === INVESTMENT SUMMARY (category subtotals only) ===
  if (yPos > pageHeight - 90) {
    doc.addPage();
    yPos = 25;
  }

  yPos += 5;
  doc.setDrawColor(0, 181, 226);
  doc.setLineWidth(1);
  doc.line(20, yPos, pageWidth - 20, yPos);
  yPos += 12;

  doc.setFontSize(13);
  doc.setTextColor(0, 181, 226);
  doc.setFont(undefined, 'bold');
  doc.text('Investment Summary', 20, yPos);
  yPos += 12;

  // Category subtotals
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.setFont(undefined, 'normal');

  const subtotals = pricing.subtotals || {};
  const subtotalEntries = [
    ['Platform & Infrastructure:', subtotals.platform],
    ['User Licenses:', subtotals.licenses],
    ['Products:', subtotals.products]
  ].filter(([_, val]) => val && val > 0);

  subtotalEntries.forEach(([label, value]) => {
    doc.text(label, 25, yPos);
    doc.text(fmt(value), pageWidth - 25, yPos, { align: 'right' });
    yPos += 7;
  });

  yPos += 3;
  doc.setDrawColor(200, 200, 200);
  doc.line(pageWidth - 100, yPos, pageWidth - 25, yPos);
  yPos += 10;

  // Annual Subscription
  doc.setFont(undefined, 'bold');
  doc.text('Annual Subscription:', 25, yPos);
  doc.setTextColor(22, 163, 74);
  doc.text(fmt(pricing.recurring?.finalPrice || 0), pageWidth - 25, yPos, { align: 'right' });
  yPos += 10;

  // One-Time Services
  if (pricing.oneTime?.finalPrice > 0) {
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    doc.text('Professional Services (One-Time):', 25, yPos);
    doc.setTextColor(0, 181, 226);
    doc.text(fmt(pricing.oneTime.finalPrice), pageWidth - 25, yPos, { align: 'right' });
    yPos += 10;
  }

  // Year 1 Total
  yPos += 3;
  doc.setDrawColor(0, 181, 226);
  doc.setLineWidth(1);
  doc.line(pageWidth - 100, yPos, pageWidth - 25, yPos);
  yPos += 12;

  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text('YEAR 1 TOTAL:', 25, yPos);
  doc.setTextColor(22, 163, 74);
  doc.setFontSize(14);
  doc.text(fmt(pricing.grandTotal?.amount || 0), pageWidth - 25, yPos, { align: 'right' });
  yPos += 8;

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont(undefined, 'normal');
  doc.text(`(Annual Recurring: ${fmt(pricing.recurring?.finalPrice || 0)}/year after Year 1)`, pageWidth - 25, yPos, { align: 'right' });

  // === MULTI-YEAR OPTIONS (if available) ===
  const multiYear = pricing.multiYearOptions?.filter(opt => opt.term > 1) || [];
  if (multiYear.length > 0 && yPos < pageHeight - 50) {
    yPos += 18;
    doc.setFontSize(10);
    doc.setTextColor(0, 181, 226);
    doc.setFont(undefined, 'bold');
    doc.text('Multi-Year Discounts Available', 20, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    multiYear.forEach(opt => {
      doc.text(`${opt.label}: ${opt.discountPercent}% off (Save ${fmt(opt.savings || 0)})`, 25, yPos);
      yPos += 6;
    });
  }

  // === KEY TERMS ===
  if (terms && yPos < pageHeight - 45) {
    yPos += 12;
    doc.setFontSize(10);
    doc.setTextColor(0, 181, 226);
    doc.setFont(undefined, 'bold');
    doc.text('Key Terms', 20, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    doc.text(`Payment: ${terms.paymentTerms || 'Net 30'}`, 25, yPos);
    yPos += 5;
    doc.text(`Billing: ${terms.billingFrequency || 'Annual'}`, 25, yPos);
    yPos += 5;
    if (terms.subscriptionStart) {
      doc.text(`Start: ${terms.subscriptionStart}`, 25, yPos);
    }
  }

  // === PAGE 2: IMPLEMENTATION & NEXT STEPS ===
  doc.addPage();
  yPos = 25;

  // Implementation Timeline
  if (q.implementation?.timeline) {
    doc.setFontSize(13);
    doc.setTextColor(0, 181, 226);
    doc.setFont(undefined, 'bold');
    doc.text('Implementation Timeline', 20, yPos);
    yPos += 5;

    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text(`Estimated Duration: ${q.implementation.timeline.totalWeeks} weeks`, 20, yPos);
    yPos += 12;

    // Milestones table
    const milestoneData = (q.implementation.timeline.milestones || []).map(m => [
      m.phase,
      m.name,
      m.activities?.slice(0, 2).join(', ') || ''
    ]);

    if (milestoneData.length > 0) {
      doc.autoTable({
        startY: yPos,
        head: [['Phase', 'Milestone', 'Key Activities']],
        body: milestoneData,
        theme: 'striped',
        headStyles: { fillColor: [0, 181, 226], textColor: 255, fontSize: 9 },
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 35 },
          2: { cellWidth: 100 }
        }
      });
      yPos = doc.lastAutoTable.finalY + 15;
    }
  }

  // Next Steps
  if (q.nextSteps?.length > 0) {
    doc.setFontSize(12);
    doc.setTextColor(0, 181, 226);
    doc.setFont(undefined, 'bold');
    doc.text('Next Steps', 20, yPos);
    yPos += 10;

    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');

    q.nextSteps.forEach((step, i) => {
      doc.setFont(undefined, 'bold');
      doc.text(`${step.step}. ${step.action}`, 25, yPos);
      doc.setFont(undefined, 'normal');
      if (step.notes) {
        doc.setTextColor(100, 100, 100);
        doc.text(`   ${step.notes}`, 25, yPos + 5);
        doc.setTextColor(60, 60, 60);
        yPos += 5;
      }
      yPos += 8;
    });
  }

  // Contact CTA
  yPos += 10;
  doc.setDrawColor(200, 200, 200);
  doc.line(20, yPos, pageWidth - 20, yPos);
  yPos += 12;

  doc.setFontSize(11);
  doc.setTextColor(30, 30, 30);
  doc.setFont(undefined, 'bold');
  doc.text('Ready to Get Started?', 20, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.setFont(undefined, 'normal');
  doc.text(`Contact ${salesRep?.name || 'your SplashBI representative'} to proceed.`, 20, yPos);
  yPos += 6;
  doc.setTextColor(0, 181, 226);
  doc.text(salesRep?.email || vendor.contact?.email || 'sales@splashbi.com', 20, yPos);

  // Footer on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont(undefined, 'normal');
    doc.text(
      `${vendor.companyName}  •  ${vendor.address.street}, ${vendor.address.city}, ${vendor.address.state} ${vendor.address.zip}`,
      pageWidth / 2, pageHeight - 12, { align: 'center' }
    );
    doc.text(vendor.website || 'www.splashbi.com', pageWidth / 2, pageHeight - 7, { align: 'center' });
  }

  // Output
  if (viewOnly) {
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  } else {
    doc.save(`${meta.quoteId}_${customer.companyName?.replace(/[^a-z0-9]/gi, '_') || 'Quote'}.pdf`);
  }

  return doc;
};
